import React from 'react'
import style from './Subproducts.module.scss'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import { unescapeTags } from '@aytm/lib/common/func'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { useFamilyAxes } from '../../hooks/useFamilyAxes'
import { LEGACY_AXIS } from '../../utils/familyAxes'
import { hiddenValueClass } from '../../utils/hiddenClass'

interface ISubproductsProps {
  questionSchema: IEcommerceQuestionSchema
  schema: IEcommerceAnswerSchema
  onSubitemSelect: (index: number) => void
}

const Subproducts = ({ questionSchema, schema, onSubitemSelect }: ISubproductsProps) => {
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()
  const { childrenProducts, axes, currentSelections, findSKUForValue, isCombinationAvailable, isHiddenInPreview } =
    useFamilyAxes({
      answer: schema,
      schema: questionSchema
    })

  function selectValue(axisName: string, value: string) {
    const targetSKU = findSKUForValue(axisName, value)
    if (!targetSKU) return
    const answerIndex = questionSchema.answers.indexOf(targetSKU)
    if (answerIndex >= 0) onSubitemSelect(answerIndex)
  }

  if (childrenProducts.length <= 1 || axes.length === 0) return null

  return (
    <>
      {axes.map((axis, axisIndex) => {
        const selectedValue = currentSelections[axis.name] || ''
        // Real Amazon shows prices only on the last (innermost) axis row; upper rows are label-only.
        const isLastAxis = axisIndex === axes.length - 1
        return (
          <React.Fragment key={axis.name}>
            {axis.name !== LEGACY_AXIS && (
              <div className={style.dismension}>
                <div className={style.dismensionName}>{axis.name}:&nbsp;</div>
                <div className={style.dismensionValue}>{selectedValue}</div>
              </div>
            )}
            <div className={`${style.childrenProducts} ${viewport.mobile ? style.childrenProductsMobile : ''}`}>
              {axis.values.map(value => {
                const matchSKU = findSKUForValue(axis.name, value)
                const compatible = isCombinationAvailable(axis.name, value)
                const isSelected = selectedValue === value
                return (
                  <button
                    key={value}
                    aria-pressed={isSelected}
                    className={`${style.productVariant} ${isSelected ? style.selected : ''} ${
                      isLastAxis ? '' : style.compact
                    } ${compatible ? '' : style.incompatible} ${hiddenValueClass(isHiddenInPreview(axis.name, value))}`}
                    onClick={() => selectValue(axis.name, value)}
                    aria-disabled={!matchSKU}
                  >
                    <div className={style.headtext}>
                      <div className={style.textContent}>{value}</div>
                    </div>
                    {isLastAxis && compatible && matchSKU ? (
                      <>
                        <div className={style.divider} />
                        <div className={style.subtext}>
                          <div
                            className={style.commonPrice}
                            dangerouslySetInnerHTML={{
                              __html: formatPriceWithLocale(
                                matchSKU.discounted_price && matchSKU.price > matchSKU.discounted_price
                                  ? matchSKU.discounted_price
                                  : matchSKU.price,
                                questionSchema.price_mask,
                                languageTag,
                                true,
                                false
                              )
                            }}
                          />
                          {matchSKU.per_count_price ? (
                            <div className={style.commonPrice}>({unescapeTags(String(matchSKU.per_count_price))})</div>
                          ) : (
                            ''
                          )}
                          {matchSKU.discounted_price && matchSKU.price > matchSKU.discounted_price ? (
                            <div
                              className={style.discountedPrice}
                              dangerouslySetInnerHTML={{
                                __html: formatPriceWithLocale(
                                  matchSKU.price,
                                  questionSchema.price_mask,
                                  languageTag,
                                  true,
                                  false
                                )
                              }}
                            />
                          ) : (
                            ''
                          )}
                        </div>
                      </>
                    ) : null}
                    {isLastAxis && (!compatible || !matchSKU) ? (
                      <>
                        <div className={style.divider} />
                        <div className={style.subtext}>
                          <div className={style.commonPrice}>{t('questions', 'ec', 'amazon', 'seeOptions')}</div>
                        </div>
                      </>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </React.Fragment>
        )
      })}
    </>
  )
}

export default Subproducts
